import { useCallback, useEffect, useState } from "react";
import type { ReportResponse, ReportRow } from "@grizzly/shared";
import { api } from "./api.js";
import type { ApiClientError } from "./http.js";
import type { MessageKey } from "../i18n/index.js";

export interface ReportDef {
  name: string;
  labelKey: MessageKey;
  financial: boolean;
}

export const REPORT_DEFS: ReportDef[] = [
  {
    name: "quote-conversion",
    labelKey: "reports.defs.quoteConversion",
    financial: false,
  },
  { name: "job-status", labelKey: "reports.defs.jobStatus", financial: false },
  { name: "wip", labelKey: "reports.defs.wip", financial: false },
  { name: "stock-reorder", labelKey: "reports.defs.stockReorder", financial: false },
  { name: "open-pos", labelKey: "reports.defs.openPos", financial: false },
  {
    name: "actual-vs-estimate",
    labelKey: "reports.defs.actualVsEstimate",
    financial: true,
  },
  { name: "invoice-aging", labelKey: "reports.defs.invoiceAging", financial: true },
  { name: "cash-activity", labelKey: "reports.defs.cashActivity", financial: true },
];

export function useReport(name: string, from: string, to: string) {
  const [data, setData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const report = await api.report(name, {
        from: from || undefined,
        to: to || undefined,
      });
      setData(report);
    } catch (err) {
      setError((err as ApiClientError).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [name, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}

export function rowColumns(rows: ReportRow[]): string[] {
  if (rows.length === 0) return [];
  return Object.keys(rows[0]!);
}
